/* ───────────────────────────────────────────────────────────────────────────
   O16 Cart Drawer — brand JS
   Handles:
   1. Cookbook upsell inline Add-to-cart (works in both empty + with-items state)
   2. Discount code pop-up modal + apply/remove + checkout carry-through
   3. Calm dispatch countdown (order before the cutoff hour, Copenhagen time → ships today)
   4. Cart drawer quantity updates (delegates to existing CartDrawerItems)
─────────────────────────────────────────────────────────────────────────── */

/* ── 1. COOKBOOK UPSELL ──────────────────────────────────────────────────── */

class O16CookbookAdd extends HTMLElement {
  constructor() {
    super();
    this._bound = this._onClick.bind(this);
  }

  // Bind in connectedCallback (not the constructor) so the listener is
  // always live for the current DOM node — including a fresh instance the
  // browser auto-upgrades after cartDrawer.renderContents() replaces the
  // drawer's innerHTML on add. disconnectedCallback tears it down so
  // replaced markup can never end up double-bound.
  connectedCallback() {
    this.addEventListener('click', this._bound);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._bound);
  }

  _onClick(event) {
    const btn = event.target.closest('[data-o16-cookbook-add]');
    if (!btn) return;
    event.preventDefault();

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    // O16 extended-delivery consent gate — the shared guard every
    // add-to-cart path in the theme calls (see
    // assets/o16-extended-delivery-consent.js for the full contract).
    // data-o16-extended-date / data-o16-extended-type are baked directly
    // onto THIS button server-side (snippets/o16-cart-reassurance.liquid),
    // only when the free-shipping nudge product resolves to a pre-order/
    // back-order slot. Resolves synchronously true otherwise, so this
    // never delays the normal case.
    if (window.O16ExtendedDelivery && typeof window.O16ExtendedDelivery.guard === 'function') {
      window.O16ExtendedDelivery.guard(btn).then((proceed) => {
        if (proceed) this._addToCart(btn, variantId);
      });
      return;
    }

    this._addToCart(btn, variantId);
  }

  _addToCart(btn, variantId) {
    // Optimistic UI
    btn.setAttribute('aria-disabled', 'true');
    btn.classList.add('loading');
    const originalLabel = btn.innerHTML;
    btn.innerHTML = '<span class="o16-spinner" aria-hidden="true"></span>';

    const cartDrawer = document.querySelector('cart-drawer');
    const sectionIds =
      cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
        ? cartDrawer.getSectionsToRender().map((section) => section.id)
        : ['cart-drawer', 'cart-icon-bubble'];

    // ONE round trip: ask /cart/add.js to render the sections we need in the
    // same response (base-theme pattern — see product-form.js) instead of a
    // slow second fetch just to re-fetch the drawer markup.
    fetch(window.routes ? window.routes.cart_add_url : '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: sectionIds,
        sections_url: window.location.pathname
      })
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok || data.status) {
          // Shopify (or the network) returned an error — surface it instead
          // of silently doing nothing.
          O16CookbookAdd._showError(data && (data.description || data.message));
          return;
        }
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          // renderContents() also opens the drawer once contents are in.
          cartDrawer.renderContents(data);
        }
        // Broadcast cart update for any other listeners (delivery-date refill
        // below, discount toggle re-init, etc).
        //
        // IMPORTANT — source MUST be 'cart-items', not a custom string:
        // cart.js's CartItems.connectedCallback() subscribes every
        // <cart-drawer-items> (and <cart-items>) instance to this same event
        // and, unless event.source === 'cart-items', runs its OWN
        // onCartUpdate() — which fetches /cart?section_id=main-cart-items and
        // blindly does `this.innerHTML = html.querySelector('cart-items').innerHTML`.
        // That is the CART PAGE's item markup (a totally different DOM shape),
        // so it stomps the <cart-drawer-items> we just correctly rendered via
        // cartDrawer.renderContents() above — this was the round-2 item-13 bug
        // ("drawer layout breaks/collapses after adding"). 'cart-items' is the
        // exact sentinel the base theme's own updateQuantity() uses for the
        // same reason (cart.js ~line 300) — reusing it here suppresses the
        // stray re-fetch on every CartItems-family listener without touching
        // cart.js / cart-drawer.js.
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          // O16 (cart-page parity): the 'cart-items' sentinel exists to stop
          // cart.js's CartItems.connectedCallback() from re-fetching and
          // stomping <cart-drawer-items>'s just-rendered markup (see the
          // comment above) — that failure mode only exists when a
          // <cart-drawer> is actually on the page. On the cart PAGE itself
          // (layout/theme.liquid never renders the drawer there), there is
          // no <cart-drawer-items> to protect, and cartDrawer.renderContents()
          // never ran — so the page's own <cart-items> (rendering this same
          // free-ship nudge inside its Order Summary aside) genuinely needs
          // to refresh via CartItems.onCartUpdate() instead of being
          // suppressed forever.
          var cartUpdateSource = cartDrawer ? 'cart-items' : 'o16-cookbook-add';
          publish(PUB_SUB_EVENTS.cartUpdate, { source: cartUpdateSource });
        }
      })
      .catch(() => {
        O16CookbookAdd._showError();
      })
      .finally(() => {
        // ALWAYS restore button state — whether the add succeeded or failed
        btn.innerHTML = originalLabel;
        btn.removeAttribute('aria-disabled');
        btn.classList.remove('loading');
      });
  }

  // Surface a visible error in the drawer's live region instead of
  // swallowing the failure.
  static _showError(message) {
    // O16 (cart-page parity): fall back to the cart PAGE's own error region
    // (#cart-errors, sections/main-cart-items.liquid) when #CartDrawer-CartErrors
    // isn't present — the two never coexist (see the class comment above).
    const el = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (!el) return;
    el.textContent =
      message ||
      (window.cartStrings && window.cartStrings.error) ||
      'Something went wrong. Please try again.';
  }
}

if (!customElements.get('o16-cookbook-add')) {
  customElements.define('o16-cookbook-add', O16CookbookAdd);
}

/* ── 2. DISCOUNT CODE MODAL + MULTI-CODE APPLY/REMOVE + CHECKOUT CARRY-OVER ─
   Extended (round 3) from a SINGLE-code system to MULTIPLE stackable/
   combinable codes. The owner-agreed accuracy rule governs everything below:
   never sum each code's solo savings client-side (inaccurate the moment
   codes interact) — always apply the FULL current set together and read
   Shopify's OWN combined total back from the cart. */

/*
  How multi-code VALIDATION + the combined struck-through total work:
  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  CONFIRMED (shopify.dev Cart AJAX API reference, "Discounts support on
  /cart/update.js"): the `discount` field on POST /cart/update.js accepts a
  COMMA-SEPARATED list — `{ discount: 'code_a,code_b' }` — which applies/
  combines multiple codes in ONE request using Shopify's own combinability
  rules (whatever each discount's admin "Combinations" settings allow), not a
  client-side guess. An empty string clears every code. This is the single
  mechanism every apply/remove/re-init round trip below uses
  (o16BuildDiscountParam + o16DiscountRoutes().update).

  APPLY (adding code N+1 on top of an already-applied set of N):
  1. GET the CURRENT cart (routes.cartJson, i.e. /cart.js) FIRST — this is
     the baseline, Shopify's own combined discount BEFORE the new code, read
     fresh rather than trusted from local memory (the authoritative-source
     rule applies to the baseline too, not just the post-apply result).
  2. POST /cart/update.js with discount = the FULL attempted set (every
     existing code + the new one), comma-joined.
  3. Compare the response's total_discount / cart_level_discount_applications
     length against the baseline from step 1 — see o16DiscountIncreased.
     INCREASED → the new code genuinely contributed → ACCEPT, persist the
     whole set. NOT increased → the new code added nothing measurable →
     REJECT: show an error, do NOT add it to the stored set, and revert the
     LIVE cart back to exactly the previous (already-valid) set so a
     rejected code never lingers server-side either (same comma-permalink
     mechanism, existing-only).

  KNOWN TRADEOFF (accepted cost, same shape as the original single-code
  system, now measured as a DELTA instead of an absolute): Shopify gives no
  storefront API to validate a code independent of cart reflection. A
  genuinely valid, genuinely combinable code whose ENTIRE marginal
  contribution lands outside the cart subtotal (the classic example is a
  free-shipping-only code) can leave total_discount's delta at 0 and will be
  REJECTED by this check — even though it would be honoured at checkout.
  Accepted cost: safer for a trust-forward brand to occasionally reject a
  rare working free-shipping-only code than to accept ANY made-up string
  into a stack forwarded to checkout unverified.

  REMOVE (dropping one code from a set of N): re-apply the REMAINING set
  (comma-joined, or an empty string if that was the last code) and re-read
  the combined total from THAT response — same mechanism, no separate
  "was this code responsible for X" computation needed (removing and
  re-reading Shopify's own new combined number is exact by construction).

  CHECKOUT CARRY-OVER for the whole set (ROUND 22 — /discount COOKIE
  REMOVED): earlier builds ALSO pointed the Checkout button at
  /discount/{codes}?redirect=/checkout and fired a matching /discount/{codes}
  cookie-set fetch on every apply/remove (o16Set/ClearActiveDiscountCodes).
  CONFIRMED LIVE (owner, WebKit + /checkout) this was pure downside: the
  /discount cookie sticks to Shopify's checkout independently of the cart,
  so REMOVING a code via the drawer left the cart clean but checkout still
  showed the old discount, and the same cookie re-applied a removed/expired
  code across later visits ("lingers ~12h"). ALSO CONFIRMED: the cart's own
  discount state (set purely via the /cart/update.js `discount` calls above)
  is BY ITSELF sufficient for checkout carry-over (apply → checkout shows
  it; remove → checkout is clean) — Shopify carries a cart's discount into
  checkout automatically, no cookie or special href needed. The Checkout
  button therefore now submits to the normal checkout route unmodified
  (o16SyncCheckoutDiscountHref is a defensive no-op — see its own comment)
  and NO code here ever sets or clears Shopify's /discount cookie. Dynamic
  checkout buttons (Shop Pay / Apple Pay, content_for_additional_checkout_
  buttons) check out the live cart directly and pick up its discount the
  same way — no wiring needed.

  PERSISTENCE across a full drawer re-render (add/remove item, unrelated cart
  mutation): the applied set lives in sessionStorage
  ('o16_discount_codes', a JSON array — transparently migrated from the
  pre-multi-code single key 'o16_discount_code' on first read) and every
  (re)connect of <o16-discount-modal> calls syncState(), which re-renders the
  list + entry label + checkout href straight from that array — the existing
  single-code "_init" pattern, simply generalised to an array. This
  deliberately does NOT re-POST /cart/update.js on every reconnect (that
  would add a network round trip to every drawer render): the set is
  self-healing instead — the very NEXT apply or remove always re-sends the
  FULL current array, so any drift silently repairs itself the next time the
  shopper touches the modal.

  SESSION-SCOPED AUTO-CLEAR for MANUAL codes only (ROUND 22 — replaces the
  cookie's incidental ~12h expiry with an intentional one, WITHOUT any
  tracking/marketing side effect): every code the shopper types THROUGH THE
  DRAWER (_applyCode's accept branch) is stamped in localStorage
  ('o16_discount_manual', code → the sessionStorage session id active at the
  moment it was applied — see o16DiscountSessionId/o16MarkCodeManual). A code
  that arrives ANY other way — most importantly a Shopify /discount/{code}
  MARKETING link, which never calls _applyCode — is never stamped, so it has
  no provenance entry at all and is permanently EXTERNAL: this file never
  reads, writes, or clears anything about it beyond the ordinary combined-
  total math above. On page/script load, o16AutoClearStaleManualCodes()
  compares each STAMPED manual code still in the server's applied set against
  the CURRENT session id: same session (reload, normal browsing, or the
  session the code was just applied in) → left alone; a DIFFERENT session
  (sessionStorage was cleared and a fresh id was just minted, i.e. genuinely
  a new visit) → that one code is dropped via the same /cart/update.js
  `discount` mechanism as every other change in this file, and forgotten
  from the provenance map. External codes are never candidates — they are
  never in that map to begin with.

  RESIDUAL, OUT-OF-SCOPE DISPLAY EDGE CASE (documented, not silently
  ignored — inherited unchanged from the single-code build): if the shopper
  makes an UNRELATED cart change (add another item, change qty) while codes
  are active, that triggers a full server-rendered drawer replace straight
  from Liquid, which can — for the same documented reason
  (cart.cart_level_discount_applications can lag for manual codes in Liquid,
  independent of this theme) — stop reflecting the combined discount in the
  struck subtotal for that ONE render, even though every code remains
  genuinely active, correctly persisted, and correctly carried to checkout.
  Fixing that fully requires editing snippets/cart-drawer.liquid, which is
  out of this file's scope (see the task's file allowlist).
*/

var O16_DISCOUNT_STORAGE_KEY = 'o16_discount_codes';
var O16_DISCOUNT_STORAGE_KEY_LEGACY = 'o16_discount_code';
// Shopify's documented combinable-code ceiling (Shopify Help Center,
// "Combining discounts"): up to 5 product/order codes + 1 shipping code per
// order. We can't distinguish code TYPE client-side, so this is a single
// soft cap on the whole stack — cheap to check up front, avoids a wasted
// round trip for an attempt Shopify would reject anyway either way.
var O16_DISCOUNT_MAX_CODES = 6;

function o16DiscountRoutes() {
  var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
  return {
    update: (window.routes && window.routes.cart_update_url) || '/cart/update.js',
    cartJson: cartUrl + '.js'
  };
}

// Round-18 (2026-07-22) discount desync fix. ROOT CAUSE: the applied-codes
// set used to live ONLY in sessionStorage, which is SESSION-scoped (cleared
// when the tab/session ends) — but Shopify's /discount/{code} cookie + the
// server cart persist far longer. Across sessions the two drift:
// sessionStorage empties while the server cart + checkout still carry the
// code, so the drawer wrongly shows "no code applied" (no chip, no remove
// button) while checkout still honours it — and re-entering the SAME code
// is wrongly rejected as "already applied" against a stale LOCAL set that
// doesn't even show it (confirmed live: /cart.js
// cart_level_discount_applications, e.g. { type: "discount_code", title:
// "WELCOME20" }, is the SAME object Liquid sees as
// cart.cart_level_discount_applications and IS what checkout honours).
//
// FIX: the SERVER cart is now the AUTHORITATIVE source — exposed via
// snippets/cart-drawer.liquid as a comma-joined data-o16-applied-codes
// attribute on [data-o16-subtotal] (see o16GetServerAppliedCodes below for
// why specifically that element). sessionStorage becomes a
// non-authoritative MIRROR, reconciled to match the server on every read.
// (Round 22: the /discount cookie this mirror used to help drive is gone —
// see the file-level comment's "CHECKOUT CARRY-OVER" section — so today
// this mirror exists purely as the offline/defensive fallback described
// below, plus the read path o16AutoClearStaleManualCodes/o16MarkCodeManual
// use to know which SPECIFIC codes are ours to manage.)
//
// Reads the data attribute SYNCHRONOUSLY off the live DOM (no fetch) —
// ALWAYS fresh because cart-drawer re-renders via the Section Rendering API
// on every cart mutation (full drawer re-render), AND
// o16PatchDiscountTotals patches .o16-totals-wrap's children (which
// [data-o16-subtotal] is one of) on every apply/remove round trip.
//
// Returns null (NOT an array) when the element/attribute is entirely
// ABSENT — e.g. the empty-cart state, which renders no totals/discount UI
// at all — so o16GetActiveDiscountCodes can distinguish "server confirms
// genuinely zero codes" (empty string → []) from "can't determine from the
// server right now" (null) and fall back to sessionStorage only in the
// latter, defensive case.
function o16GetServerAppliedCodes() {
  var subtotal = document.querySelector('[data-o16-subtotal]');
  if (!subtotal || subtotal.dataset.o16AppliedCodes == null) return null;
  return subtotal.dataset.o16AppliedCodes
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean);
}

// Reads the AUTHORITATIVE applied-codes array. Server-sourced (via
// o16GetServerAppliedCodes above) whenever available — the common case —
// which ALSO reconciles the sessionStorage mirror to match, so the mirror
// (and the manual-provenance staleness check that reads it — see FIX B
// below) never drifts from what's actually applied. Falls back to
// sessionStorage ONLY when the server data
// attribute is entirely absent (defensive — see o16GetServerAppliedCodes).
// Always returns an array (never null/undefined) so callers never need a
// defensive check. Still transparently migrates the pre-multi-code
// single-code key on first read in the fallback path — old sessions
// carrying exactly one active code upgrade silently into the new array
// shape with no user-visible change.
function o16GetActiveDiscountCodes() {
  var serverCodes = o16GetServerAppliedCodes();
  if (serverCodes !== null) {
    o16PersistDiscountCodes(serverCodes);
    return serverCodes;
  }

  try {
    var raw = sessionStorage.getItem(O16_DISCOUNT_STORAGE_KEY);
    if (raw) {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    }
    var legacy = sessionStorage.getItem(O16_DISCOUNT_STORAGE_KEY_LEGACY);
    if (legacy) {
      var migrated = [legacy];
      o16PersistDiscountCodes(migrated);
      sessionStorage.removeItem(O16_DISCOUNT_STORAGE_KEY_LEGACY);
      return migrated;
    }
  } catch (_) {
    // sessionStorage unavailable / malformed JSON — fail to an empty set
    // rather than throw, same fail-safe rule as the rest of this file.
  }
  return [];
}

// Storage-only write (no network side effect) — split out so the reject-
// revert path in _applyCode can be built from the same primitives without
// implying a shape change happened.
function o16PersistDiscountCodes(codes) {
  try {
    if (codes && codes.length) {
      sessionStorage.setItem(O16_DISCOUNT_STORAGE_KEY, JSON.stringify(codes));
    } else {
      sessionStorage.removeItem(O16_DISCOUNT_STORAGE_KEY);
    }
  } catch (_) {
    // sessionStorage unavailable — checkout carry-over still works this
    // visit regardless: the live cart itself (set via /cart/update.js)
    // carries its own discount state into checkout with no cookie needed.
  }
}

// Called ONLY after the FULL set is CONFIRMED to have increased the combined
// discount (see _applyCode's o16DiscountIncreased check) — never
// optimistically before that. Persists the array to the sessionStorage
// mirror. ROUND 22: this used to ALSO fire a Shopify /discount/{codes}
// cookie-setter fetch for checkout carry-over — REMOVED. Confirmed live
// (owner, WebKit + /checkout) that cookie was the actual cause of "remove
// doesn't clear checkout" (it re-applies the old code at checkout even
// after the cart itself is clean) and of codes lingering across visits. The
// live cart's own discount state, set by the /cart/update.js call that
// already ran before this function is called, is BY ITSELF sufficient for
// checkout carry-over — confirmed both ways (apply carries through, remove
// leaves checkout clean) — so no cookie of any kind is set here anymore.
function o16SetActiveDiscountCodes(codes) {
  o16PersistDiscountCodes(codes);
}

// Best-effort local clear of the WHOLE set (spec item 5 — "Remove ALL →
// revert subtotal to normal"). ROUND 22: no longer clears a /discount
// cookie (removed — see o16SetActiveDiscountCodes above); also wipes the
// manual-provenance map (o16MarkCodeManual) since /cart/update.js's
// discount:'' — the caller's own request, made just before this runs —
// already cleared every code server-side, manual and external alike, so
// nothing is left to track.
function o16ClearActiveDiscountCodes() {
  try {
    sessionStorage.removeItem(O16_DISCOUNT_STORAGE_KEY);
    sessionStorage.removeItem(O16_DISCOUNT_STORAGE_KEY_LEGACY);
  } catch (_) {
    // ignore
  }
  o16WriteManualProvenance({});
}

// Shopify's own CONFIRMED comma syntax for /cart/update.js's `discount`
// field (see file-level comment) — the single mechanism every apply/remove
// round trip below sends. `[]` → '' (Shopify's documented "clear all").
function o16BuildDiscountParam(codes) {
  return (codes || []).join(',');
}

/* ── FIX B (round 22): MANUAL-code provenance + session-scoped auto-clear ──
   See the file-level comment's "SESSION-SCOPED AUTO-CLEAR" section for the
   full design. Two storage keys, both O16-owned and touched nowhere else in
   this theme:
     - O16_DISCOUNT_SESSION_KEY (sessionStorage) — one random id per browser
       session, minted the first time it's read in a session and stable
       across reloads/navigation within that same session.
     - O16_DISCOUNT_MANUAL_KEY (localStorage) — { code_lowercased: sessionId
       it was applied in }, written ONLY from _applyCode's accept branch
       (o16MarkCodeManual). A code that never goes through _applyCode (e.g.
       a Shopify /discount/{code} marketing link) never gets an entry here —
       it stays permanently EXTERNAL and is never a candidate for auto-clear.
   Nothing here reads/writes any OTHER cookie or storage key. */
var O16_DISCOUNT_SESSION_KEY = 'o16_discount_session_id';
var O16_DISCOUNT_MANUAL_KEY = 'o16_discount_manual';

// Returns the current browser-session id, minting and persisting one on
// first read. Stable for the lifetime of the sessionStorage session (tab
// close/session end clears it — a fresh id on the next visit is what makes
// a manual code from a PRIOR session read as stale below). Returns null if
// sessionStorage is unavailable — every caller below treats null as "skip,
// never guess" rather than falling back to any other signal.
function o16DiscountSessionId() {
  try {
    var id = sessionStorage.getItem(O16_DISCOUNT_SESSION_KEY);
    if (!id) {
      id = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
      sessionStorage.setItem(O16_DISCOUNT_SESSION_KEY, id);
    }
    return id;
  } catch (_) {
    return null;
  }
}

function o16ReadManualProvenance() {
  try {
    var raw = localStorage.getItem(O16_DISCOUNT_MANUAL_KEY);
    var parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
}

function o16WriteManualProvenance(map) {
  try {
    localStorage.setItem(O16_DISCOUNT_MANUAL_KEY, JSON.stringify(map || {}));
  } catch (_) {
    // localStorage unavailable — provenance tracking is a best-effort
    // convenience on top of the core apply/remove flow, never blocks it.
  }
}

// Stamps `code` as MANUALLY applied through the drawer THIS session — called
// ONLY from _applyCode's accept branch, for the single newly-typed code
// (never for the rest of the existing set, whose own provenance — manual
// from an earlier session, or none at all if external — must never be
// overwritten just because it happened to be re-sent alongside a new code).
function o16MarkCodeManual(code) {
  var sessionId = o16DiscountSessionId();
  if (!sessionId) return;
  var map = o16ReadManualProvenance();
  map[code.toLowerCase()] = sessionId;
  o16WriteManualProvenance(map);
}

// Drops `code`'s provenance entry (if any) — called on an explicit
// drawer-driven remove, so a later re-apply of the same code starts clean.
// A no-op for a code with no entry (external, or already forgotten).
function o16ForgetCodeManual(code) {
  var map = o16ReadManualProvenance();
  var key = String(code).toLowerCase();
  if (map[key] == null) return;
  delete map[key];
  o16WriteManualProvenance(map);
}

// THE session-scoped auto-clear. Safe by construction for paid marketing:
// only ever considers codes that are (a) currently applied server-side AND
// (b) present in OUR OWN manual-provenance map — i.e. codes we ourselves
// recorded as drawer-applied. A code is stale only if its recorded session
// id doesn't match the CURRENT one (sessionStorage was cleared since —
// genuinely a new visit). Same-session codes (a reload, or the very apply
// that just stamped it) always match and are left untouched — that IS the
// "never cleared in the session it was applied" grace, with no separate
// timer needed. External/marketing codes have no provenance entry and are
// therefore never inspected, never cleared, never touched in any way.
function o16AutoClearStaleManualCodes() {
  if (o16AutoClearStaleManualCodes._inFlight) return;

  var serverCodes = o16GetServerAppliedCodes();
  if (!serverCodes || !serverCodes.length) return;

  var sessionId = o16DiscountSessionId();
  if (!sessionId) return; // sessionStorage unavailable — never guess

  var manualMap = o16ReadManualProvenance();
  var stale = serverCodes.filter((code) => {
    var recordedSession = manualMap[code.toLowerCase()];
    return recordedSession != null && recordedSession !== sessionId;
  });
  if (!stale.length) return;

  var remaining = serverCodes.filter((code) => stale.indexOf(code) === -1);
  var routes = o16DiscountRoutes();

  o16AutoClearStaleManualCodes._inFlight = true;
  fetch(routes.update, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      discount: o16BuildDiscountParam(remaining),
      sections: ['cart-drawer'],
      sections_url: window.location.pathname
    })
  })
    .then((res) => res.json())
    .then((data) => {
      stale.forEach((code) => o16ForgetCodeManual(code));
      o16PersistDiscountCodes(remaining);
      o16PatchDiscountTotals(data.sections);
      if (remaining.length) o16ReconcileSubtotalDisplay(data);
      o16SyncDiscountUI();
      // Same sentinel as every other cart-mutating call in this file — see
      // the identical comment on _applyCode's accept branch.
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
      }
    })
    .catch(() => {
      // Network failure — leave everything exactly as-is; re-attempt on the
      // next load rather than clearing anything without server confirmation.
    })
    .finally(() => {
      o16AutoClearStaleManualCodes._inFlight = false;
    });
}

// Surgical patch: replaces ONLY .o16-totals-wrap's innerHTML with the fresh
// server-rendered markup from the /cart/update.js `sections` response, so the
// (sibling, untouched) discount modal never loses its open/closed state or
// focus while the subtotal / discount-list re-renders with real Liquid truth.
function o16PatchDiscountTotals(sections) {
  var html = sections && sections['cart-drawer'];
  if (!html) return false;
  var freshWrap = new DOMParser().parseFromString(html, 'text/html').querySelector('.o16-totals-wrap');
  // O16 (cart-page parity): prefer the drawer-scoped match, but fall back to
  // an unscoped one for the cart PAGE, which has no #CartDrawer at all
  // (layout/theme.liquid never renders the drawer on request.page_type ==
  // 'cart'). The two are mutually exclusive — never both in the DOM at
  // once — so this fallback can never accidentally patch the wrong instance.
  var liveWrap = document.querySelector('#CartDrawer .o16-totals-wrap') || document.querySelector('.o16-totals-wrap');
  if (!freshWrap || !liveWrap) return false;
  liveWrap.innerHTML = freshWrap.innerHTML;
  return true;
}

// Raw-JSON accessors — `cart` is the RAW /cart/update.js or /cart.js JSON
// response object itself (top-level fields, NOT Liquid's rendered `sections`
// markup — see the file-level comment for why that distinction matters).
// Fail-safe: any missing/malformed field reads as 0, never throws.
function o16DiscountAmountCents(cart) {
  return cart && typeof cart.total_discount === 'number' ? cart.total_discount : 0;
}

function o16DiscountApplicationsCount(cart) {
  return cart && Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications.length : 0;
}

// THE accept/reject decision for a code being ADDED to an existing set —
// compares Shopify's own combined discount AFTER the full attempted set
// against the BASELINE read before it (see file-level "APPLY" steps 1-3).
// Deliberately amount-only (not also apps-count-only, unlike the discarded
// v2 single-code o16DiscountReflected): this guarantees `increased` implies
// newCents > 0, which in turn guarantees o16ReconcileSubtotalDisplay's own
// `total_discount > 0` guard always fires right after an accept — otherwise
// a rare code whose only visible effect is a NEW cart_level_discount_
// applications entry with a ~0 allocated amount could get accepted here yet
// read back as "not reflected" on the very next syncState(), which
// auto-clears anything not reflected — an accept-then-vanish flash. Any
// missing/malformed field fails CLOSED (returns false) — ambiguous evidence
// must never accept a code.
function o16DiscountIncreased(baselineCart, resultCart) {
  return o16DiscountAmountCents(resultCart) > o16DiscountAmountCents(baselineCart);
}

// Formats a cents integer via the theme's own global money formatter
// (assets/global.js, loaded site-wide) and theme.moneyFormat (set from
// shop.money_format in layout/theme.liquid) — the same pattern
// assets/o16-sticky-buybar.js already uses for JS-side price formatting on
// this theme. Falls back to a plain decimal string if that global is ever
// unavailable (should not happen on this theme, but never throw).
function o16FormatMoney(cents) {
  if (typeof formatMoney === 'function') {
    try {
      return formatMoney(cents, (window.theme && window.theme.moneyFormat) || '');
    } catch (_) {
      // fall through to the plain fallback below
    }
  }
  return (cents / 100).toFixed(2);
}

// Gap-filler for the manual-code display limitation described in the
// file-level comment above: o16PatchDiscountTotals just replaced
// .o16-totals-wrap with FRESH LIQUID-rendered markup, which is correct for
// automatic/storefront-visible discounts but can still show "not reflected"
// for a combined set we've ALREADY confirmed accepted via the raw JSON. Only
// steps in for exactly that gap — if Liquid's own patch already reflects the
// discount (data-o16-discount-reflected="true"), this is a no-op and never
// downgrades an already-correct, correctly money_with_currency-formatted
// value. Works identically for 1 code or N combined codes: total_discount is
// already Shopify's own COMBINED figure across the whole cart-level set.
//
// EXTENDED 2026-07-13 for the combined [current] / [struck original] /
// [green −%] format (cart-drawer.liquid): the "original" this function
// reconciles is now full-list-price-inclusive (compare-at), not code-only.
// It reads the compare-at-inclusive total from data-o16-full-list-cents —
// the value Liquid JUST wrote fresh onto this exact element via
// o16PatchDiscountTotals, called immediately before this function every time
// (see _applyCode / _removeCode below). Safe to trust with no lag risk,
// UNLIKE cart.cart_level_discount_applications (the field this whole
// function exists to work around) — compare-at pricing never depends on
// discount-application reflection, so Liquid always gets it right immediately.
//
// FIXED 2026-09-03: fullListCents (data-o16-full-list-cents) is already the
// PRE-code full-list subtotal, and cart.total_price is already NET of any
// applied discount code — so the struck original is fullListCents alone.
// Adding cart.total_discount on top double-counted the code's amount
// (mirrors the same fix in cart-drawer.liquid's o16_subtotal_original_cents).
function o16ReconcileSubtotalDisplay(cart) {
  if (!cart || typeof cart.total_discount !== 'number' || cart.total_discount <= 0) return;
  if (typeof cart.total_price !== 'number') return;
  var subtotalEl = document.querySelector('[data-o16-subtotal]');
  if (!subtotalEl || subtotalEl.dataset.o16DiscountReflected === 'true') return;

  var currentEl = subtotalEl.querySelector('[data-o16-subtotal-current]');
  var originalEl = subtotalEl.querySelector('[data-o16-subtotal-original]');
  var savingsEl = subtotalEl.querySelector('[data-o16-subtotal-savings]');

  var fullListCents = parseInt(subtotalEl.dataset.o16FullListCents, 10);
  if (isNaN(fullListCents) || fullListCents < cart.total_price) fullListCents = cart.total_price;
  var originalCents = fullListCents;

  if (currentEl) currentEl.textContent = o16FormatMoney(cart.total_price);
  if (originalEl) {
    if (originalCents > cart.total_price) {
      originalEl.textContent = o16FormatMoney(originalCents);
      originalEl.hidden = false;
    } else {
      originalEl.hidden = true;
    }
  }
  if (savingsEl) {
    if (originalCents > cart.total_price) {
      // Math.floor to match Liquid's own integer `divided_by` truncation
      // (see cart-drawer.liquid's o16_savings_pct / snippets/price.liquid) —
      // keeps this JS-reconciled percent numerically identical to whatever
      // the next full Liquid re-render of the same cart state would show.
      var pct = Math.floor(((originalCents - cart.total_price) * 100) / originalCents);
      savingsEl.textContent = '−' + pct + '%';
      savingsEl.hidden = false;
    } else {
      savingsEl.hidden = true;
    }
  }
  subtotalEl.dataset.o16DiscountReflected = 'true';
  subtotalEl.dataset.o16DiscountAmountFormatted = o16FormatMoney(cart.total_discount);
}

// ROUND 22 — DEFENSIVE NO-OP. Used to point the Checkout button at
// /discount/{codes}?redirect=/checkout on every sync; REMOVED per the
// file-level "CHECKOUT CARRY-OVER" comment (confirmed live: that href — and
// the matching cookie it relied on — was the actual cause of "remove
// doesn't clear checkout"). The live cart's own discount state already
// carries into checkout with no href needed, so the button now always
// submits its normal <form id="CartDrawer-Form"> checkout action
// unmodified. This function is kept (rather than deleted, along with its
// call sites in syncState/o16SyncDiscountUI) ONLY to strip a stale
// data-discount-href a shopper might still be carrying from localStorage-
// cached markup / bfcache from before this fix shipped — it never sets the
// attribute again.
function o16SyncCheckoutDiscountHref() {
  var btn = document.getElementById('o16-checkout-btn');
  if (!btn) return;
  btn.removeAttribute('data-discount-href');
}

// Re-syncs whatever <o16-discount-modal> is currently in the DOM (its own
// connectedCallback already does this on (re)connect — this covers the case
// where the modal itself wasn't replaced but .o16-totals-wrap was, e.g. after
// an unrelated qty change refreshes discount-reflection data server-side).
function o16SyncDiscountUI() {
  o16SyncCheckoutDiscountHref();
  var modal = document.querySelector('o16-discount-modal');
  if (modal && typeof modal.syncState === 'function') {
    modal.syncState();
  } else {
    // Modal not upgraded yet (unlikely, but keep the struck subtotal correct
    // regardless) — still reflect original/current visibility from data
    // attrs. Same rule as O16DiscountModal.syncState() above: never
    // force-hide (that would hide genuine compare-at-only savings) — only
    // ever un-hide when a code is confirmed reflected.
    var codes = o16GetActiveDiscountCodes();
    var subtotal = document.querySelector('[data-o16-subtotal]');
    if (codes.length && subtotal && subtotal.dataset.o16DiscountReflected === 'true') {
      var original = subtotal.querySelector('[data-o16-subtotal-original]');
      var savings = subtotal.querySelector('[data-o16-subtotal-savings]');
      if (original) original.hidden = false;
      if (savings) savings.hidden = false;
    }
  }
}

class O16DiscountModal extends HTMLElement {
  connectedCallback() {
    this._panel = this.querySelector('.o16-discount-modal__panel');
    this._entry = this.querySelector('[data-o16-discount-entry]');
    this._entryLabel = this.querySelector('[data-o16-discount-entry-label]');
    this._applied = this.querySelector('[data-o16-discount-applied]');
    this._list = this.querySelector('[data-o16-discount-list]');
    this._input = this.querySelector('[data-o16-discount-input]');
    this._applyBtn = this.querySelector('[data-o16-discount-apply]');
    this._feedback = this.querySelector('[data-o16-discount-feedback]');
    this._returnFocus = null;

    this._onClick = this._onClick.bind(this);
    this._onKeyup = this._onKeyup.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this.addEventListener('click', this._onClick);
    this.addEventListener('keyup', this._onKeyup);
    this.addEventListener('keydown', this._onKeydown);

    // Re-render the applied-codes list + entry label + checkout href from
    // sessionStorage on every (re)connect — a full drawer re-render
    // (add/remove item) recreates this element fresh. See the file-level
    // "PERSISTENCE" note for why this deliberately does NOT re-POST to
    // /cart/update.js here (self-healing on the next apply/remove instead).
    this.syncState();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('keyup', this._onKeyup);
    this.removeEventListener('keydown', this._onKeydown);
  }

  _onClick(event) {
    if (event.target.closest('[data-o16-discount-close]')) {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.target.closest('[data-o16-discount-apply]')) {
      this._applyCode();
      return;
    }
    var removeBtn = event.target.closest('[data-o16-discount-remove-code]');
    if (removeBtn) {
      if (removeBtn.getAttribute('aria-disabled') === 'true') return;
      this._removeCode(removeBtn.dataset.o16DiscountRemoveCode, removeBtn);
    }
  }

  // Escape closes the MODAL only — stopPropagation before the keyup bubbles
  // to <cart-drawer>, whose own keyup Escape handler would otherwise close
  // the whole drawer underneath it (cart-drawer.js).
  _onKeyup(event) {
    if ((event.key === 'Escape' || event.key === 'Esc') && !this.hidden) {
      event.stopPropagation();
      this.close();
    }
  }

  _onKeydown(event) {
    if (event.key === 'Enter' && event.target === this._input) {
      event.preventDefault();
      this._applyCode();
    }
  }

  // The entry input is now ALWAYS visible (multi-code stacking spec — no
  // more "hide entry once a code is applied" state), so it's always the
  // right default/recovery focus target.
  _defaultFocusTarget() {
    return this._input || this._panel;
  }

  open(trigger) {
    this._returnFocus = trigger || document.activeElement;
    this.hidden = false;
    var focusTarget = this._defaultFocusTarget();
    if (typeof trapFocus === 'function' && this._panel) {
      trapFocus(this._panel, focusTarget);
    } else if (focusTarget) {
      focusTarget.focus();
    }
  }

  close() {
    if (this.hidden) return;
    this.hidden = true;
    var drawerInner = document.querySelector('.drawer__inner');
    // Prefer a FRESH lookup of the trigger over the captured _returnFocus
    // reference: the trigger lives inside .o16-totals-wrap, which an
    // apply/remove round-trip replaces wholesale while the modal is open
    // (see o16PatchDiscountTotals) — so _returnFocus can be a detached,
    // stale node by the time close() runs. .focus() on a detached node is a
    // silent no-op, which would otherwise strand focus on whatever control
    // this modal itself is about to hide.
    var freshTrigger = document.querySelector('[data-o16-discount-trigger]');
    var focusEl = freshTrigger && freshTrigger.isConnected ? freshTrigger : this._returnFocus;
    if (drawerInner && typeof trapFocus === 'function') {
      // Re-establishes the drawer's OWN focus trap (scoped back to the whole
      // drawer) and returns focus to whatever opened the modal (spec: "return
      // focus to the trigger on close").
      trapFocus(drawerInner, focusEl || undefined);
    } else if (focusEl) {
      focusEl.focus();
    }
    this._returnFocus = null;
  }

  // ADD a code on top of whatever set is already applied — see the
  // file-level "APPLY" steps for the full accuracy-method walkthrough.
  _applyCode() {
    if (!this._input || !this._applyBtn) return;
    var code = this._input.value.trim();
    if (!code) return;

    var existing = o16GetActiveDiscountCodes();

    // Duplicate guard — compare case-insensitively (Shopify discount codes
    // aren't case sensitive) without touching what gets SENT/stored, which
    // stays exactly what the shopper typed (unchanged from the single-code
    // build's normalization).
    if (existing.some((c) => c.toLowerCase() === code.toLowerCase())) {
      this._setFeedback('That code is already applied.', 'error');
      return;
    }

    if (existing.length >= O16_DISCOUNT_MAX_CODES) {
      this._setFeedback('You can apply up to ' + O16_DISCOUNT_MAX_CODES + ' codes to one order.', 'error');
      return;
    }

    this._applyBtn.setAttribute('aria-disabled', 'true');
    this._setFeedback('', '');

    var routes = o16DiscountRoutes();
    var attemptSet = existing.concat([code]);

    // NOTE: nothing is persisted or carried to checkout before this whole
    // round trip resolves AND confirms an increase — see the file-level
    // comment above. A code is only ever accepted once Shopify's own
    // combined total genuinely grew because of it.
    fetch(routes.cartJson, { headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then((baselineCart) =>
        fetch(routes.update, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            discount: o16BuildDiscountParam(attemptSet),
            sections: ['cart-drawer'],
            sections_url: window.location.pathname
          })
        })
          .then((res) => res.json())
          .then((data) => ({ baselineCart, data }))
      )
      .then(({ baselineCart, data }) => {
        this._applyBtn.removeAttribute('aria-disabled');

        if (o16DiscountIncreased(baselineCart, data)) {
          // ACCEPT — Shopify's own combined total_discount genuinely grew
          // for the full attempted set vs the pre-add baseline.
          o16PatchDiscountTotals(data.sections);
          o16ReconcileSubtotalDisplay(data);
          // Belt-and-suspenders against the accept-then-vanish flash
          // documented on o16DiscountIncreased above — `increased` is
          // already authoritative for THIS round trip; never let
          // syncState's own (weaker, Liquid-dependent) inference undo it.
          var subtotalEl = document.querySelector('[data-o16-subtotal]');
          if (subtotalEl) subtotalEl.dataset.o16DiscountReflected = 'true';
          o16SetActiveDiscountCodes(attemptSet);
          // FIX B (round 22) — stamp ONLY the just-typed code as manual-
          // this-session. The rest of `existing` keeps whatever provenance
          // (or lack of it) it already had — see o16MarkCodeManual's comment.
          o16MarkCodeManual(code);
          if (this._input) this._input.value = '';
          this.syncState(); // reads the just-patched/reconciled DOM for the "you save X" copy

          // IMPORTANT — source MUST be 'cart-items', not 'discount': cart.js's
          // CartItems.connectedCallback() subscribes every <cart-drawer-items>
          // to this event and, unless event.source === 'cart-items', runs its
          // own onCartUpdate() — which fetches /cart?section_id=main-cart-items
          // (the CART PAGE's item markup) and stomps <cart-drawer-items>'s
          // innerHTML with it. See the identical fix/comment on O16CookbookAdd
          // above (round-2 item-13 bug) — same root cause, same sentinel fix.
          //
          // Fired ONLY on ACCEPT (never on REJECT — see the else branch's own
          // comment below for why): this is what previously caused a REAL bug
          // (present since the single-code build, made newly visible while
          // building this multi-code version): publishing cartUpdate
          // unconditionally after EVERY apply attempt — including a reject —
          // triggers the OTHER cartUpdate subscriber ~100ms later
          // (o16SyncDiscountUI → modal.syncState(), see the bottom of this
          // file), which — called WITHOUT { preserveFeedback: true } — wiped
          // the just-set rejection error message right back to '' a moment
          // after it appeared (a visible flash-then-vanish). A reject reverts
          // the cart to EXACTLY its pre-attempt state (see the revert fetch
          // below), so no OTHER listener (delivery date, subtotal-fit, other
          // <cart-drawer-items> instances) has anything genuine to re-sync —
          // skipping the broadcast entirely on reject is correct, not just a
          // race-condition workaround.
          if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
          }
        } else {
          // REJECT — the full attempted set shows NO greater combined effect
          // than the existing set already did. Keep whatever the shopper
          // typed in the input (so they can see/correct it), never persist
          // the new code, and revert the LIVE cart back to exactly the
          // previous (already-valid) set so a rejected code never lingers
          // server-side either. Deliberately does NOT publish cartUpdate —
          // see the ACCEPT branch's comment above for why.
          this._setFeedback("That code isn't valid, has expired, or can't combine with your current codes.", 'error');

          fetch(routes.update, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              discount: o16BuildDiscountParam(existing),
              sections: ['cart-drawer'],
              sections_url: window.location.pathname
            })
          })
            .then((res) => res.json())
            .then((revertData) => {
              o16PatchDiscountTotals(revertData.sections);
              if (existing.length) o16ReconcileSubtotalDisplay(revertData);
              // The stored set itself never changed — re-sync everything
              // ELSE (list, entry label, checkout href) from it, but
              // preserve the rejection message we just set above (a normal
              // full syncState() would overwrite it with "you save X" the
              // instant `existing` is itself a valid, reflected set).
              this.syncState({ preserveFeedback: true });
            })
            .catch(() => {
              this.syncState({ preserveFeedback: true });
            });
        }
      })
      .catch(() => {
        this._applyBtn.removeAttribute('aria-disabled');
        // Network/parse failure — fail CLOSED: never carry over an
        // unconfirmed code.
        this._setFeedback('Could not check that code. Please try again.', 'error');
      });
  }

  // REMOVE a single code from the applied set — re-applies the REMAINING
  // set (or clears entirely if `code` was the last one) and re-reads
  // Shopify's own new combined total from that same response; exact by
  // construction, no separate "was this code responsible for X" math.
  _removeCode(code, triggerBtn) {
    if (!code) return;
    var existing = o16GetActiveDiscountCodes();
    var remaining = existing.filter((c) => c.toLowerCase() !== String(code).toLowerCase());
    if (remaining.length === existing.length) return; // not found — no-op guard

    if (triggerBtn) triggerBtn.setAttribute('aria-disabled', 'true');
    if (this._applyBtn) this._applyBtn.setAttribute('aria-disabled', 'true');

    var routes = o16DiscountRoutes();

    fetch(routes.update, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        discount: o16BuildDiscountParam(remaining), // '' when remaining is empty — clears every code
        sections: ['cart-drawer'],
        sections_url: window.location.pathname
      })
    })
      .then((res) => res.json())
      .then((data) => {
        // FIX B (round 22) — this code is explicitly leaving the applied
        // set via the drawer; drop its provenance entry (no-op if it never
        // had one, e.g. external) so a later re-apply starts clean.
        o16ForgetCodeManual(code);
        if (remaining.length) {
          o16SetActiveDiscountCodes(remaining);
          o16PatchDiscountTotals(data.sections);
          o16ReconcileSubtotalDisplay(data);
        } else {
          // Remove ALL — revert subtotal to normal (round 22: no cookie to
          // clear anymore — see o16ClearActiveDiscountCodes's own comment).
          o16ClearActiveDiscountCodes();
          o16PatchDiscountTotals(data.sections);
        }
        if (this._applyBtn) this._applyBtn.removeAttribute('aria-disabled');
        this.syncState();

        // IMPORTANT — source MUST be 'cart-items' — see the identical
        // comment in _applyCode above (round-2 item-13 bug, same fix).
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
        }
      })
      .catch(() => {
        // Same fail-safe convention as the original single-code remove flow:
        // a REMOVE always "succeeds" locally even if this specific
        // /cart/update.js round trip fails over the network — checkout
        // carry-over no longer depends on any cookie (round 22), only on
        // the live cart's own state, which this specific request was trying
        // to update; sessionStorage still mirrors intent for the drawer UI.
        o16ForgetCodeManual(code);
        if (remaining.length) {
          o16SetActiveDiscountCodes(remaining);
        } else {
          o16ClearActiveDiscountCodes();
        }
        if (this._applyBtn) this._applyBtn.removeAttribute('aria-disabled');
        if (triggerBtn) triggerBtn.removeAttribute('aria-disabled');
        this.syncState();
      });
  }

  // Renders the applied-codes list from an array of code strings. Full
  // rebuild every call — the set is at most O16_DISCOUNT_MAX_CODES long and
  // this only runs on apply/remove/re-init, never a hot path. textContent
  // only (never innerHTML) for the code string itself — defensive against
  // any unusual characters in a shopper-typed code.
  _renderList(codes) {
    if (!this._list) return;
    this._list.innerHTML = '';
    codes.forEach((code) => {
      var li = document.createElement('li');
      li.className = 'o16-discount-modal__list-item';

      var codeEl = document.createElement('span');
      codeEl.className = 'o16-discount-modal__list-code';
      codeEl.textContent = code;

      var removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'o16-discount-modal__remove o16-discount-modal__list-remove';
      removeBtn.setAttribute('data-o16-discount-remove-code', code);
      removeBtn.setAttribute('aria-label', 'Remove code ' + code);
      removeBtn.textContent = 'Remove';

      li.appendChild(codeEl);
      li.appendChild(removeBtn);
      this._list.appendChild(li);
    });
  }

  // Re-reads sessionStorage + the sibling .o16-subtotal's data attributes
  // (rendered fresh by Liquid on every server round trip, or reconciled by
  // o16ReconcileSubtotalDisplay) and updates: the list, the entry label, the
  // feedback copy, and the checkout carry-over. Safe to call whether the
  // modal is open or closed.
  //
  // options.preserveFeedback — skip writing new feedback text (used by the
  // reject-revert path in _applyCode, which already set an error message
  // this call must not overwrite with "you save X" for the still-valid
  // existing set).
  syncState(options) {
    var preserveFeedback = !!(options && options.preserveFeedback);
    // Round-18: is `codes` below SERVER-sourced (the data attribute on
    // [data-o16-subtotal] was present) or the sessionStorage fallback
    // (attribute entirely absent — see o16GetServerAppliedCodes)? Gates the
    // "not reflected → auto-clear" branch further down — see its own
    // comment for why that branch is provably unreachable (not just
    // gated-off) in the server-sourced path by construction: both
    // data-o16-applied-codes and data-o16-discount-reflected are computed
    // from cart.cart_level_discount_applications on the SAME element in the
    // SAME Liquid render (cart-drawer.liquid), and discount_code-type
    // applications are a subset of ALL applications, so codes.length > 0
    // there mathematically implies reflected === true. Kept as an explicit
    // gate (not relied on implicitly) so a future change to either
    // computation can't silently reintroduce the stale-clear bug.
    var isServerSourced = o16GetServerAppliedCodes() !== null;
    var codes = o16GetActiveDiscountCodes();
    var subtotal = document.querySelector('[data-o16-subtotal]');
    var original = subtotal && subtotal.querySelector('[data-o16-subtotal-original]');
    var savings = subtotal && subtotal.querySelector('[data-o16-subtotal-savings]');
    var reflected = !!subtotal && subtotal.dataset.o16DiscountReflected === 'true';
    var amountFormatted = (subtotal && subtotal.dataset.o16DiscountAmountFormatted) || '';
    // Bundle/line-level discount fix (2026-07): a discount that only ever
    // shows up as a per-line allocation (e.g. a code targeting a Shopify
    // Bundle product) reflects in cart.total_discount but NEVER appears in
    // cart.cart_level_discount_applications — so `reflected` above is false
    // for it even though it's genuinely still applied. Without this signal,
    // the sessionStorage-fallback branch below wrongly auto-cleared such
    // codes on every reload (chip silently dropped while the discount kept
    // working). data-o16-cart-has-discount is a SEPARATE, broader signal
    // (cart.total_discount > 0, cart-drawer.liquid) that stays true for
    // bundle/line-level discounts too — used ONLY to gate the auto-clear
    // below, never to drive "You save X" copy (that stays reflected-only,
    // since amountFormatted is cart-level-only and would read 0,00 here).
    var cartHasDiscount = !!subtotal && subtotal.dataset.o16CartHasDiscount === 'true';

    // original/savings now represent a COMBINED total (item compare-at sale
    // pricing + a reflected discount code — see cart-drawer.liquid's
    // o16_subtotal_original_cents), not code-only like before. A fresh
    // Liquid render always precedes this call (either the full-drawer
    // re-render that just recreated this element, or o16PatchDiscountTotals
    // immediately before o16ReconcileSubtotalDisplay in the apply/remove
    // flow) and already sets the correct hidden/shown state for compare-at
    // savings with NO code involved — that has zero lag risk, so it must
    // NEVER be force-hidden here just because codes.length is 0 (the
    // pre-2026-07-13 behaviour did exactly that — a real bug once this
    // element started doing double duty). Only ever act to UN-hide, for the
    // one gap this function still needs to help with: a reflected code whose
    // fresh render (or JS reconcile) already proved a genuine combined
    // saving. See the file-level "RESIDUAL, OUT-OF-SCOPE" comment for the
    // one case this still can't fully close.
    if (codes.length && reflected) {
      if (original) original.hidden = false;
      if (savings) savings.hidden = false;
    }

    // If the modal is open and focus currently sits on a control this sync
    // is about to remove/replace (e.g. the just-clicked row Remove button,
    // whose row is about to be rebuilt), the browser drops focus to <body>
    // once that node is detached — stranding keyboard users and breaking
    // ESC-to-close (the keyup no longer bubbles through the modal). The drop
    // happens on the NEXT frame, not synchronously, so the recovery check
    // below is deferred to requestAnimationFrame — checking synchronously
    // reads a stale (pre-drop) activeElement and never fires.
    var hadFocusInModal = !this.hidden && this.contains(document.activeElement);

    this._renderList(codes);
    if (this._applied) this._applied.hidden = codes.length === 0;
    if (this._entryLabel) {
      this._entryLabel.textContent = codes.length ? 'Add another code' : 'Enter your code';
    }

    if (!codes.length) {
      if (!preserveFeedback) this._setFeedback('', '');
    } else if (reflected) {
      if (!preserveFeedback) {
        this._setFeedback(
          amountFormatted ? `You save ${amountFormatted}.` : 'Your codes are applied.',
          'success'
        );
      }
    } else if (isServerSourced) {
      // Round-18: `codes` came straight from the server (see the top of
      // this method) — the server ITSELF just confirmed these codes ARE
      // applied, so there is nothing to auto-clear here. This branch is
      // unreachable in practice (see the isServerSourced comment above for
      // why codes.length > 0 there always implies reflected === true), but
      // is spelled out explicitly rather than silently falling into the
      // sessionStorage-era auto-clear below, which must never fire against
      // a server-confirmed set.
    } else if (cartHasDiscount) {
      // Bundle/line-level discount fix: NOT reflected in
      // cart_level_discount_applications, but cart.total_discount proves
      // the cart genuinely still has an active discount — keep the codes,
      // do NOT clear. Generic copy only: amountFormatted is cart-level-only
      // (would read 0,00 here), so "You save X" would be misleading.
      if (!preserveFeedback) {
        this._setFeedback('Your codes are applied.', 'success');
      }
    } else {
      // Sessionstorage-FALLBACK path only (server data attribute was
      // entirely absent — see o16GetServerAppliedCodes), AND the cart
      // genuinely has NO active discount of any kind (cartHasDiscount
      // false too) — e.g. the cart changed underneath it (an item removed
      // dropped the order below a minimum-spend threshold, or the discount
      // was removed elsewhere) so it no longer applies at all. Same
      // fail-closed rule as the Apply check above: auto-clear the WHOLE set
      // rather than show stale "applied" copy for codes that no longer do
      // anything.
      o16ClearActiveDiscountCodes();
      codes = [];
      this._renderList(codes);
      if (this._applied) this._applied.hidden = true;
      if (this._entryLabel) this._entryLabel.textContent = 'Enter your code';
      if (!preserveFeedback) {
        this._setFeedback('Your codes are no longer valid for this cart and were removed.', 'error');
      }
    }

    if (hadFocusInModal) {
      var self = this;
      // Double rAF: measured empirically — Chrome doesn't drop focus to
      // <body> synchronously with the DOM change above, nor even by the
      // NEXT frame; it settles one frame after that. A single rAF (or a
      // synchronous check) reads a stale activeElement and never recovers.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (document.activeElement === document.body) {
            var recovered = self._defaultFocusTarget();
            if (recovered) recovered.focus();
          }
        });
      });
    }

    o16SyncCheckoutDiscountHref();
  }

  _setFeedback(msg, type) {
    if (!this._feedback) return;
    this._feedback.textContent = msg;
    this._feedback.className = 'o16-discount-modal__feedback';
    if (type === 'success') {
      this._feedback.classList.add('o16-discount-modal__feedback--success');
    } else if (type === 'error') {
      this._feedback.classList.add('o16-discount-modal__feedback--error');
    }
  }
}

if (!customElements.get('o16-discount-modal')) {
  customElements.define('o16-discount-modal', O16DiscountModal);
}

// Trigger ("Do you have a code?") — event delegation from document so it
// keeps working after ANY cart re-render (the trigger button lives inside
// the surgically-patched .o16-totals-wrap; a full drawer re-render
// recreates everything). The DOMContentLoaded one-shot pattern would orphan
// after renderContents()/innerHTML replaces the DOM.
// ROUND 22: the #o16-checkout-btn discount-href interception that used to
// live here (redirecting to /discount/{codes}?redirect=/checkout) is
// REMOVED — see o16SyncCheckoutDiscountHref's comment. The button is a
// plain type="submit" bound to form="CartDrawer-Form" (cart-drawer.liquid)
// and now always takes its natural, unmodified path to checkout.
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-o16-discount-trigger]');
  if (trigger) {
    e.preventDefault();
    const modal = document.querySelector('o16-discount-modal');
    if (modal && typeof modal.open === 'function') modal.open(trigger);
  }
});

// ── CAUSE MODAL ──────────────────────────────────────────────────────────
// "Pick a cause. We cover it." — the second trigger sharing the drawer's
// trigger row with the discount trigger above (.o16-drawer-trigger-row,
// cart-drawer.liquid). Mirrors <o16-discount-modal>'s open/close/focus-trap/
// Escape behaviour exactly, but has no apply/remove/syncState — the panel
// content is an intentionally EMPTY placeholder (see
// snippets/o16-cause-modal.liquid) for a script to populate later.
class O16CauseModal extends HTMLElement {
  connectedCallback() {
    this._panel = this.querySelector('.o16-cause-modal__panel');
    this._returnFocus = null;

    this._onClick = this._onClick.bind(this);
    this._onKeyup = this._onKeyup.bind(this);
    this.addEventListener('click', this._onClick);
    this.addEventListener('keyup', this._onKeyup);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('keyup', this._onKeyup);
  }

  _onClick(event) {
    if (event.target.closest('[data-o16-cause-close]')) {
      event.preventDefault();
      this.close();
    }
  }

  // Escape closes the MODAL only — stopPropagation before the keyup bubbles
  // to <cart-drawer>, whose own keyup Escape handler would otherwise close
  // the whole drawer underneath it (cart-drawer.js). Same reasoning as
  // <o16-discount-modal>._onKeyup above.
  _onKeyup(event) {
    if ((event.key === 'Escape' || event.key === 'Esc') && !this.hidden) {
      event.stopPropagation();
      this.close();
    }
  }

  open(trigger) {
    this._returnFocus = trigger || document.activeElement;
    this.hidden = false;
    /* THIRD-PARTY WIDGETS STEP ASIDE (owner 2026-09-14). Klaviyo's popup is a
       fixed layer on document.body with a z-index far above this modal, and in
       a fresh browser it covers the whole screen — measured: our close button
       sat under Klaviyo's own heading, so the shopper could see the cause
       picker and not get out of it. Same body hook the extended-delivery modal
       already uses (assets/o16-extended-delivery-modal.css), with our OWN
       class so the two features can never clear each other's state. Removed on
       every close path below. */
    document.body.classList.add('o16-cause-modal-open');
    if (typeof trapFocus === 'function' && this._panel) {
      trapFocus(this._panel, this._panel);
    } else if (this._panel) {
      this._panel.focus();
    }
  }

  close() {
    if (this.hidden) return;
    this.hidden = true;
    document.body.classList.remove('o16-cause-modal-open');
    var drawerInner = document.querySelector('.drawer__inner');
    // Prefer a FRESH lookup of the trigger over the captured _returnFocus
    // reference — same reasoning as <o16-discount-modal>.close() above (the
    // trigger row lives inside .o16-totals-wrap, which an apply/remove
    // round-trip can replace wholesale while this modal is open).
    var freshTrigger = document.querySelector('[data-o16-cause-trigger]');
    var focusEl = freshTrigger && freshTrigger.isConnected ? freshTrigger : this._returnFocus;
    if (drawerInner && typeof trapFocus === 'function') {
      trapFocus(drawerInner, focusEl || undefined);
    } else if (focusEl) {
      focusEl.focus();
    }
    this._returnFocus = null;
  }
}

if (!customElements.get('o16-cause-modal')) {
  customElements.define('o16-cause-modal', O16CauseModal);
}

// Trigger ("Pick a cause. We cover it.") — same document-delegation pattern
// as the discount trigger above, for the same reason (survives every cart
// drawer re-render).
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-o16-cause-trigger]');
  if (trigger) {
    e.preventDefault();
    const modal = document.querySelector('o16-cause-modal');
    if (modal && typeof modal.open === 'function') modal.open(trigger);
  }
});

// Keep the checkout carry-over + modal "applied" state in sync across EVERY
// cart mutation (add / qty change / discount apply-remove), not just our own
// — a full drawer re-render can happen from unrelated actions (e.g. the
// cookbook upsell add-to-cart) while codes are already active. Mirrors the
// EXPECTED DELIVERY DATE re-fill pattern below (subscribe once pub/sub is
// available; fall back to DOMContentLoaded if it isn't yet).
(function () {
  function attachDiscountSyncListener() {
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(o16SyncDiscountUI, 100));
      return true;
    }
    return false;
  }

  if (!attachDiscountSyncListener()) {
    document.addEventListener('DOMContentLoaded', () => attachDiscountSyncListener());
  }

  // FIX B (round 22) — session-scoped auto-clear, run ONCE per page load
  // (this script re-evaluates fresh on every navigation in this theme, so
  // "once per script eval" already IS "once per page/cart load" — see the
  // file-level "SESSION-SCOPED AUTO-CLEAR" comment for the full design).
  // Async (fetches /cart/update.js only if it actually finds a stale code);
  // its own completion handler calls o16SyncDiscountUI() again, so the
  // (synchronous) initial sync below may briefly show a code that's about
  // to be cleared a moment later — same one-render-lag tradeoff already
  // accepted elsewhere in this file (see o16DiscountIncreased's comment),
  // not a new one.
  o16AutoClearStaleManualCodes();

  // Initial sync — codes can already be in sessionStorage from an earlier
  // page view in this tab session.
  o16SyncDiscountUI();
})();

/* ── 3. CALM DISPATCH COUNTDOWN ─────────────────────────────────────────── */
// "Order before [cutoff]:00 on weekdays → ships today · expected delivery [date]"
// Cutoff hour is owner-tunable via settings.o16_dispatch_cutoff_hour (default 13:00).
// All times computed in Europe/Copenhagen timezone.
// No blinking, no red, no sound. Pure factual information.

/**
 * Returns the current time components in Copenhagen timezone.
 * Returns { hours, minutes, weekday } where weekday: 0=Sun … 6=Sat.
 */
function o16CopenhagenNow() {
  const now = new Date();
  // Use Intl to extract Copenhagen local time fields
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Copenhagen',
    hour: 'numeric',
    minute: 'numeric',
    weekday: 'short',
    hour12: false
  }).formatToParts(now);

  const get = (type) => {
    const p = parts.find((p) => p.type === type);
    return p ? p.value : null;
  };

  const hours = parseInt(get('hour'), 10);
  const minutes = parseInt(get('minute'), 10);
  const weekdayStr = get('weekday'); // 'Mon', 'Tue', …, 'Sun'
  const weekdayMap = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 0 };
  const weekday = weekdayMap[weekdayStr] !== undefined ? weekdayMap[weekdayStr] : -1;

  return { hours, minutes, weekday };
}

class O16DispatchCountdown extends HTMLElement {
  connectedCallback() {
    this._el = this.querySelector('[data-o16-countdown]');
    if (!this._el) return;

    // Cutoff hour is owner-tunable via settings.o16_dispatch_cutoff_hour,
    // passed through as a data attribute on this custom element. Default 13.
    const parsedHour = parseInt(this.getAttribute('data-cutoff-hour'), 10);
    this._cutoffHour = Number.isNaN(parsedHour) ? 13 : parsedHour;

    this._update();
    // Update every 30 seconds — calm, not ticking
    this._timer = setInterval(() => this._update(), 30000);
  }

  disconnectedCallback() {
    if (this._timer) clearInterval(this._timer);
  }

  _update() {
    const block = this.querySelector('[data-o16-dispatch-block]');
    if (!block) return;

    const cutoffHour = this._cutoffHour;

    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this._el.textContent = `before ${cutoffHour}:00`;
      return;
    }

    const { hours, minutes, weekday } = o16CopenhagenNow();
    const isWeekday = weekday >= 1 && weekday <= 5;

    if (!isWeekday) {
      block.setAttribute('data-weekend', 'true');
      block.removeAttribute('data-past-cutoff');
      return;
    }

    // Clear state attributes (may be re-rendering after a previously set state)
    block.removeAttribute('data-weekend');
    block.removeAttribute('data-past-cutoff');

    // Minutes since midnight (Copenhagen)
    const minutesSinceMidnight = hours * 60 + minutes;
    const cutoffMinutes = cutoffHour * 60;

    // Always count down to the NEXT cutoff — today's if it is still ahead,
    // otherwise tomorrow's. The owner prefers the live countdown over the
    // "next-day dispatch" message after the cutoff (2026-06-22).
    let diffMinutes = cutoffMinutes - minutesSinceMidnight;
    if (diffMinutes <= 0) {
      diffMinutes += 24 * 60;
    }
    const h = Math.floor(diffMinutes / 60);
    const m = diffMinutes % 60;

    if (h > 0) {
      this._el.textContent = `${h}h ${m}m`;
    } else {
      this._el.textContent = `${m}m`;
    }
  }
}

customElements.define('o16-dispatch-countdown', O16DispatchCountdown);

/* ── 4. EXPECTED DELIVERY DATE ───────────────────────────────────────────── */
// Uses event delegation from document so it re-init after every re-render.
// Fills [data-o16-delivery-date] elements whenever they appear in DOM.

/**
 * Add n weekdays to a Date, returning a new Date.
 * Weekday check uses Copenhagen timezone.
 */
function o16AddWeekdays(date, n) {
  const d = new Date(date);
  let added = 0;
  while (added < n) {
    d.setDate(d.getDate() + 1);
    // Determine if this new day is a weekday in Copenhagen
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Copenhagen',
      weekday: 'short'
    }).formatToParts(d);
    const weekdayStr = (parts.find((p) => p.type === 'weekday') || {}).value;
    if (weekdayStr !== 'Sat' && weekdayStr !== 'Sun') added++;
  }
  return d;
}

function o16FillDeliveryDate() {
  const els = document.querySelectorAll('[data-o16-delivery-date]');
  if (!els.length) return;

  // Owner rule 2026-06-22: estimated delivery is always +3 business days from
  // today, weekends excluded (o16AddWeekdays skips Sat/Sun in Copenhagen TZ —
  // e.g. ordering Thu lands on Tue, ordering Fri lands on Wed).
  const daysToAdd = 3;
  const delivery = o16AddWeekdays(new Date(), daysToAdd);

  const dateStr = delivery.toLocaleDateString('en-GB', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });

  els.forEach((el) => {
    el.textContent = dateStr;
  });
}

// Initial fill
document.addEventListener('DOMContentLoaded', o16FillDeliveryDate);

// Re-fill after every Shopify section re-render (cart updates swap the DOM)
// PUB_SUB_EVENTS.cartUpdate fires after renderContents completes.
// We also observe mutations on the cart drawer for safety.
(function () {
  function attachCartUpdateListener() {
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        // Small delay to let DOM settle after renderContents
        setTimeout(o16FillDeliveryDate, 100);
      });
      return true;
    }
    return false;
  }

  // Try immediately; fall back to DOMContentLoaded if pub/sub not yet available
  if (!attachCartUpdateListener()) {
    document.addEventListener('DOMContentLoaded', () => attachCartUpdateListener());
  }
})();

/* ── 5. SCROLL CUE / MOBILE FOLD TOGGLE ────────────────────────────────────
   DESKTOP (unchanged, owner 2026-06-22): shows a cue on the products/footer
   separator while the cart item list overflows and isn't scrolled to the
   bottom. Re-inits on every cart re-render (custom element reconnects).
   Click scrolls the list down.

   MOBILE FIX (owner round-5, 2026-07-16): this always measured
   `<cart-drawer-items>` directly, which works on tall (desktop) viewports
   where component-cart-drawer.css gives it `overflow-y:auto; height:100%`.
   But that same file has `@media screen and (max-height: 650px)` — true on
   most phones — which flips <cart-drawer-items> to `overflow:visible;
   height:auto` (it no longer scrolls at all — it just grows to fit its
   content) and makes `.drawer__inner` the REAL scroll container instead
   (`overflow:auto` there). Measuring the wrong element always read
   scrollHeight - clientHeight ≈ 0, so `.is-visible` never toggled on
   mobile. Fix: resolve which element is ACTUALLY the live scroll container
   right now via its computed overflow-y, rather than hardcoding either
   element or that 650px breakpoint value — this keeps working even if the
   breakpoint changes later, and re-resolves on resize (viewport height can
   change mid-session, e.g. rotating the phone, or Safari's URL bar
   show/hide). Desktop path is untouched: <cart-drawer-items> still resolves
   there exactly as before.

   MOBILE FOLD TOGGLE (owner round-15, 2026-07-17): on ≤749px, this same
   element is repurposed as a persistent FOLD affordance instead — clicking
   it toggles `.o16-cart--folded` on the PERSISTENT <cart-drawer> element
   (never on anything inside #CartDrawer, which cartDrawer.renderContents()
   fully replaces on every cart mutation — a class living there would be
   lost on every add/remove/qty change) to collapse/expand the
   informational middle band (trust line, dispatch/shipping, free-ship
   nudge, "have a code?", tax/duties notes — see assets/o16-cart-drawer.css
   for exactly what hides). Desktop keeps the EXACT existing scroll-by
   behaviour — the fold logic below is gated on a live viewport-width check,
   not a one-time flag, so a breakpoint crossed mid-session (resize/rotate)
   is always honoured correctly.
   This custom element itself gets destroyed + recreated on every cart
   re-render (it lives inside #CartDrawer), so fold STATE must be read back
   from — not just written to — the persistent <cart-drawer> element on
   every (re)connect, or a freshly reconnected instance would show
   "expanded" ARIA while the drawer is actually still folded.
   A separate one-time watcher (bottom of this block) resets the fold class
   whenever the drawer closes, so it ALWAYS reopens expanded (owner
   requirement — folding is a per-viewing convenience, never a persisted
   preference across drawer open/close cycles). */
class O16ScrollCue extends HTMLElement {
  connectedCallback() {
    this._onClick = () => {
      if (isMobileViewport()) {
        this._toggleFold();
        return;
      }
      const el = this._scroller;
      if (!el) return;
      el.scrollBy({ top: el.clientHeight * 0.7, behavior: 'smooth' });
    };
    this.addEventListener('click', this._onClick);

    // This element carries role="button" (snippets/cart-drawer.liquid) —
    // Enter/Space must activate it exactly like a click, matching native
    // <button> keyboard semantics.
    this._onKeydown = (event) => {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        this._onClick();
      }
    };
    this.addEventListener('keydown', this._onKeydown);

    this._onResize = () => this._wire();
    window.addEventListener('resize', this._onResize, { passive: true });
    this._wire();
    // Defer initial read until layout settles (drawer opens with animation)
    requestAnimationFrame(() => this._wire());
    this._t = setTimeout(() => this._wire(), 350);

    // Reflect the PERSISTENT fold state on this freshly (re)connected
    // instance's ARIA attributes — see the class comment above.
    this._syncAriaFromPersistedState();
    ensureFoldResetOnClose(this._drawer());
  }

  disconnectedCallback() {
    this._unwireScroller();
    window.removeEventListener('resize', this._onResize);
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('keydown', this._onKeydown);
    clearTimeout(this._t);
  }

  _drawer() {
    return this.closest('cart-drawer');
  }

  _toggleFold() {
    const drawer = this._drawer();
    if (!drawer) return;
    const folded = drawer.classList.toggle('o16-cart--folded');
    this._setAria(folded);
  }

  _setAria(folded) {
    this.setAttribute('aria-expanded', folded ? 'false' : 'true');
    this.setAttribute('aria-label', folded ? 'Show cart details' : 'Collapse cart details');
  }

  _syncAriaFromPersistedState() {
    const drawer = this._drawer();
    const folded = !!(drawer && drawer.classList.contains('o16-cart--folded'));
    this._setAria(folded);
  }

  /* Resolve the element that is ACTUALLY the scroll container right now —
     see the class comment above for why this can't be hardcoded. */
  _resolveScroller() {
    const itemsEl = document.querySelector('cart-drawer-items');
    if (itemsEl) {
      const style = window.getComputedStyle(itemsEl);
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
        return itemsEl;
      }
    }
    return document.querySelector('cart-drawer .drawer__inner');
  }

  _wire() {
    const next = this._resolveScroller();
    if (next === this._scroller) {
      this._update();
      return;
    }
    this._unwireScroller();
    this._scroller = next;
    if (!this._scroller) return;
    this._onScroll = this._update.bind(this);
    this._scroller.addEventListener('scroll', this._onScroll, { passive: true });
    this._update();
  }

  _unwireScroller() {
    if (this._scroller && this._onScroll) {
      this._scroller.removeEventListener('scroll', this._onScroll);
    }
  }

  _update() {
    const el = this._scroller;
    if (!el) return;
    const overflow = el.scrollHeight - el.clientHeight;
    const atBottom = el.scrollTop >= overflow - 8;
    // Desktop only — on mobile this class is overridden to always-visible
    // by assets/o16-cart-drawer.css (max-width:749px), so computing it here
    // unconditionally is harmless (CSS wins) and keeps this method simple.
    this.classList.toggle('is-visible', overflow > 8 && !atBottom);
  }
}

/* Matches the theme's own ≤749px CSS breakpoint convention exactly (see
   assets/o16-cart-drawer.css) — a live check (not cached) so a mid-session
   resize/orientation change is always honoured. */
function isMobileViewport() {
  return window.innerWidth <= 749;
}

/* One-time-per-drawer watcher: the fold class must never survive a
   close→reopen cycle (owner requirement — always reopens expanded). Rather
   than hook cart-drawer.js's close() (a base-theme file this fix
   deliberately doesn't touch), observe <cart-drawer>'s own class attribute
   — close() removes "active" from it — and enforce the invariant "never
   folded while not active" reactively. Wired ONCE per <cart-drawer>
   element (persistent, so a flag on the element itself is sufficient even
   though O16ScrollCue instances come and go on every cart re-render). */
function ensureFoldResetOnClose(drawer) {
  if (!drawer || drawer.dataset.o16FoldWatcherWired) return;
  drawer.dataset.o16FoldWatcherWired = '1';
  try {
    const observer = new MutationObserver(() => {
      if (!drawer.classList.contains('active') && drawer.classList.contains('o16-cart--folded')) {
        drawer.classList.remove('o16-cart--folded');
      }
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  } catch (e) {
    /* MutationObserver unavailable — the fold class simply persists across
       a close/reopen in that rare case; never breaks anything else. */
  }
}

if (!customElements.get('o16-scroll-cue')) {
  customElements.define('o16-scroll-cue', O16ScrollCue);
}

/* ── 6. SUBTOTAL FIT-TO-ONE-LINE (round-3 fix, 2026-07-09) ────────────────
   GOVERNING RULE (owner-confirmed via screenshot at 375px): the whole
   subtotal row — "SUBTOTAL (Do you have a code?)" + the current/struck-
   original price pair — must read on ONE line for every price magnitude.
   Small totals already fit; large ones ("941,60 DKK / 1.177,00 DKK" and up)
   were wrapping the whole VALUE down to a second row under the label. The
   price pair itself (.o16-subtotal) already never splits internally
   (flex-wrap:nowrap + white-space:nowrap — left intact); the bug was the
   OUTER .o16-totals row wrapping the value below the label.

   FIX: .o16-totals is flex-wrap:nowrap by default (o16-cart-drawer.css).
   The label (.o16-totals__label) keeps its natural size (flex-shrink:0);
   this routine measures the width remaining next to it and shrinks ONLY
   the value's (.o16-totals__value.o16-subtotal) font-size — which the
   struck-through original inherits proportionally via its own em-based
   sizing (o16-cart-drawer.css) — down to a readable floor of 1rem/10px.
   Reused pattern: reset-to-natural, measure scrollWidth vs available width,
   scale, clamp — same routine shape as assets/o16-header-taglines.js and
   assets/o16-collection-intro.js.

   LAST RESORT: if even the floor can't fit label + value on one line (a
   pathologically long total well beyond any realistic case — tuned to hold
   up to ~5-digit-thousands totals like "19.946,40 DKK / 21.183,00 DKK" at
   375px), .o16-totals--wrapped is added, restoring the PRE-fix behavior
   (value drops to its own row) rather than letting text clip/overlap. */
(function () {
  var MIN_REM = 1; /* font-size floor, in rem — 1rem = 10px on this theme's root */
  var SAFETY = 0.98;
  /* Kept in sync with .o16-totals's own `gap` in o16-cart-drawer.css — see
     that rule's comment for why this is deliberately tight (4px/0.4rem, not
     the usual 8px/0.8rem rhythm): the label alone eats over half the row at
     375px, so the ~5-digit-thousands worst case needs every spare px. */
  var MIN_GAP_PX = 4;
  var RESIZE_DEBOUNCE_MS = 150;

  function rootFontSizePx() {
    return parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 10;
  }

  function o16FitSubtotalRow() {
    var row = document.querySelector('.o16-totals');
    if (!row) return; /* empty-cart state renders no totals row */
    var label = row.querySelector('.o16-totals__label');
    var value = row.querySelector('.o16-totals__value.o16-subtotal');
    if (!label || !value) return;

    /* Reset to the natural CSS-declared state first — clears any previously
       JS-applied font-size AND any previous last-resort wrap fallback, so a
       cart change that SHRINKS the total (or a resize that widens the
       drawer) can grow back to full size / one line rather than staying
       stuck at whatever a larger total previously required. */
    row.classList.remove('o16-totals--wrapped');
    value.style.fontSize = '';

    var minPx = MIN_REM * rootFontSizePx();
    var naturalPx = parseFloat(window.getComputedStyle(value).fontSize);
    if (!naturalPx) return; /* not laid out yet */

    var rowWidth = row.clientWidth;
    /* getBoundingClientRect().width, NOT scrollWidth: .o16-totals__value.o16-subtotal
       has overflow:visible (the default — it is never given overflow:hidden/
       auto/scroll), and scrollWidth is only guaranteed accurate on elements
       that establish an actual scroll container. On an overflow:visible flex
       item, scrollWidth can silently equal the (possibly squeezed) box size
       instead of the true content extent. getBoundingClientRect().width is
       reliable here specifically BECAUSE the CSS deliberately leaves this
       element's min-width at its flex-item default (`auto`) — a nowrap flex
       child can never be compressed below its content's min-content size, so
       the rendered box always equals the content's true natural width at the
       current font-size (see the CSS comment on .o16-totals__value.o16-subtotal). */
    var labelWidth = label.getBoundingClientRect().width;
    var valueWidth = value.getBoundingClientRect().width;
    if (rowWidth <= 0 || labelWidth <= 0 || valueWidth <= 0) return; /* not laid out yet */

    var available = rowWidth - labelWidth - MIN_GAP_PX;

    if (valueWidth <= available) return; /* already fits at natural size — leave it CSS-driven */

    var fittedPx = Math.max(minPx, Math.min(naturalPx, naturalPx * (available / valueWidth) * SAFETY));
    value.style.fontSize = fittedPx + 'px';

    /* Re-measure the ACTUAL rendered result rather than trusting the scale
       math alone — this is what catches the "even the floor overflows"
       case and triggers the last-resort wrap fallback. */
    if (labelWidth + value.getBoundingClientRect().width + MIN_GAP_PX > rowWidth) {
      row.classList.add('o16-totals--wrapped');
    }
  }

  var resizeTimer = null;
  function onResize() {
    if (resizeTimer) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(o16FitSubtotalRow, RESIZE_DEBOUNCE_MS);
  }

  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);

  if (
    document.fonts &&
    typeof document.fonts.ready === 'object' &&
    typeof document.fonts.ready.then === 'function'
  ) {
    document.fonts.ready.then(o16FitSubtotalRow);
  }

  /* Re-fit after every cart mutation (add / qty change / discount apply-
     remove) — PUB_SUB_EVENTS.cartUpdate fires from every code path in this
     theme that replaces the drawer's markup (base product-form.js / cart.js,
     plus the O16CookbookAdd / O16DiscountModal re-publishes above), so this
     one subscription covers every "drawer render" moment — same pattern as
     the o16FillDeliveryDate / o16SyncDiscountUI listeners above. */
  function attachSubtotalFitListener() {
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        setTimeout(o16FitSubtotalRow, 100);
      });
      return true;
    }
    return false;
  }

  if (!attachSubtotalFitListener()) {
    document.addEventListener('DOMContentLoaded', attachSubtotalFitListener);
  }

  /* Initial fit — the drawer can render with items already in the cart on
     first page load (server-rendered), before any cartUpdate ever fires. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', o16FitSubtotalRow);
  } else {
    o16FitSubtotalRow();
  }
})();
